import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ENV } from '../core-ng/injection-token';
import { ElementType, QuestionType } from '@hyper-inferno/survey-elements-core';

@Injectable({
  providedIn: 'root',
})
export class SurveyBuilderService {
  private http = inject(HttpClient);
  environment = inject(ENV);

  //
  // get blocks
  getBlocks(survey_id: number): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.environment.consoleApi}/survey-builder/get-blocks/${survey_id}`,
    );
  }

  // Create block
  createBlock(payload: { name: string; survey_id: number }): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/survey-builder/create-block`,
      payload,
    );
  }

  // update block
  updateBlock(payload: {
    name: string;
    randomize_pages: boolean;
    id: number;
    survey_id: number;
  }): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/survey-builder/update-block`,
      payload,
    );
  }

  // delete block
  deleteBlock(payload: { id: number; survey_id: number }): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/survey-builder/delete-block`,
      payload,
    );
  }

  // reorderBlocks
  reorderBlocks(
    payload: {
      id: number;
      rank: number;
      survey_id: number;
    }[],
  ): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/survey-builder/reorder-blocks`,
      payload,
    );
  }

  //
  //
  // get page
  getPages(survey_id: number): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.environment.consoleApi}/survey-builder/get-pages/${survey_id}`,
    );
  }

  getPageByBlockId(payload: {
    block_id: number;
    survey_id: number;
  }): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/survey-builder/get-page`,
      payload,
    );
  }

  // Create page (with element)
  createPage(payload: {
    block_id: number;
    elementTypeId: string;
    survey_id: number;
    insertAfterPageId?: number;
  }): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/survey-builder/create-page`,
      payload,
    );
  }

  // update page
  updatePage(payload: {
    id: number;
    name: string;
    randomize_elements: boolean;
    block_id: number;
    survey_id: number;
  }): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/survey-builder/update-page`,
      payload,
    );
  }

  // delete page
  deletePage(payload: {
    id: number;
    block_id: number;
    survey_id: number;
  }): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/survey-builder/delete-page`,
      payload,
    );
  }

  //
  getElements(survey_id: number): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.environment.consoleApi}/survey-builder/get-elements/${survey_id}`,
    );
  }

  getElementsByListLineage(list_id: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.environment.consoleApi}/survey-builder/get-elements-by-list-lineage/${list_id}`,
    );
  }

  getElementByPageId(payload: {
    page_id: number;
    survey_id: number;
  }): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/survey-builder/get-element`,
      payload,
    );
  }

  // Create element on existing page
  createElement(payload: {
    page_id: number;
    elementTypeId: string;
    survey_id: number;
    insertAfterElementId?: number;
  }): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/survey-builder/create-element`,
      payload,
    );
  }

  createMixedGridElement(payload: {
    elementTypeId: string;
    survey_id: number;
    option_id: string;
  }): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/survey-builder/create-mixed-grid-element`,
      payload,
    );
  }

  updateElement(payload: {
    id: number;
    name: string;
    page_id: number;
    survey_id: number;
  }): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/survey-builder/update-element`,
      payload,
    );
  }

  // delete element
  deleteElement(payload: {
    id: number;
    page_id: number;
    survey_id: number;
  }): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/survey-builder/delete-element`,
      payload,
    );
  }

  // change question type
  changeQuestionType(payload: {
    page_id: number;
    element_id: number;
    elementTypeId: string;
    survey_id: number;
    insertAfterElementId?: number;
  }): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/survey-builder/change-question-type`,
      payload,
    );
  }

  // fix element settings
  fixElementSettings(payload: {
    element_id: number;
    survey_id: number;
  }): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/survey-builder/fix-element-settings`,
      payload,
    );
  }

  // get Endings
  getEndings(survey_id: number): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.environment.consoleApi}/survey-builder/get-endings/${survey_id}`,
    );
  }

  // Create Endings
  createEnding(payload: { survey_id: number }): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/survey-builder/create-ending`,
      payload,
    );
  }

  // update Endings
  updateEnding(payload: {
    name: string;
    id: string;
    survey_id: number;
    page_id: number;
  }): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/survey-builder/update-ending`,
      payload,
    );
  }

  // delete Endings
  deleteEnding(payload: {
    id: string;
    survey_id: number;
    page_id: number;
  }): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/survey-builder/delete-ending`,
      payload,
    );
  }

  // reorder Endings
  reorderEndings(
    payload: {
      id: string;
      rank: number;
      survey_id: number;
    }[],
  ): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/survey-builder/reorder-endings`,
      payload,
    );
  }

  //

  // ============= REORDER  =============

  // Reorder pages
  reorderPages(
    updates: {
      id: number;
      rank: number;
      block_id: number;
      survey_id: number;
    }[],
  ): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/survey-builder/reorder-pages`,
      updates,
    );
  }

  // Reorder elements
  reorderElements(
    updates: {
      id: number;
      rank: number;
      survey_id: number;
      page_id: number;
    }[],
  ): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/survey-builder/reorder-elements`,
      updates,
    );
  }

  // Move page to another block
  movePageToBlock(payload: {
    page_id: number;
    targetBlockId: number;
    newRank: number;
  }): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/survey-builder/move-page-to-block`,
      payload,
    );
  }

  // Move element to another page
  moveElementToPage(payload: {
    element_id: number;
    targetPageId: number;
    newRank: number;
  }): Observable<any> {
    return this.http.post(
      `${this.environment.consoleApi}/survey-builder/move-element-to-page`,
      payload,
    );
  }
  //get all lists by survey id and list type
  getLists(payload: {
    survey_id: number;
    listType: string;
  }): Observable<any[]> {
    return this.http.post<any[]>(
      `${this.environment.consoleApi}/survey-builder/get-lists-by-surveyId-listType`,
      payload,
    );
  }
  // Get list options
  getListOptions(list_id: string): Observable<any[]> {
    return this.http.post<any[]>(
      `${this.environment.consoleApi}/survey-builder/get-list-options`,
      { list_id },
    );
  }

  private readonly API_BASE = inject(ENV).sigma_exporter_api;

  duplicateSurvey(survey_id: number) {
    const payload = { survey_id };
    return this.http.post<any[]>(
      `${this.environment.sigma_exporter_api}/duplicate-generate-yaml`,
      payload,
    );
  }

  duplicateBlock(block_id: number, survey_id: number) {
    const payload = { block_id, survey_id };
    return this.http.post<any[]>(
      `${this.environment.sigma_exporter_api}/duplicate-generate-yaml`,
      payload,
    );
  }
  duplicatePage(survey_id: number, block_id: number, page_id: number) {
    const payload = { block_id, survey_id, page_id };
    return this.http.post<any[]>(
      `${this.environment.sigma_exporter_api}/duplicate-generate-yaml`,
      payload,
    );
  }
  duplicateElement(
    survey_id: number,
    block_id: number,
    page_id: number,
    element_id: number,
  ) {
    const payload = { block_id, survey_id, page_id, element_id };
    return this.http.post<any[]>(
      `${this.environment.sigma_exporter_api}/duplicate-generate-yaml`,
      payload,
    );
  }

  importQuestionBankBlock(
    block_id: number,
    survey_id: number,
    target_survey_id: any,
  ) {
    const payload = { block_id, survey_id, target_survey_id };
    return this.http.post<any[]>(
      `${this.environment.sigma_exporter_api}/import-question-bank`,
      payload,
    );
  }
  importQuestionBankPage(
    survey_id: number,
    block_id: number,
    page_id: number,
    target_survey_id: number,
    target_block_id: number,
  ) {
    const payload = {
      survey_id,
      block_id,
      page_id,
      target_survey_id,
      target_block_id,
    };
    return this.http.post<any[]>(
      `${this.environment.sigma_exporter_api}/import-question-bank`,
      payload,
    );
  }
  importQuestionBankElement(
    survey_id: number,
    block_id: number,
    page_id: number,
    element_id: number,
    target_survey_id: number,
    target_block_id: number,
    target_page_id: number,
  ) {
    const payload = {
      survey_id,
      block_id,
      page_id,
      element_id,
      target_survey_id,
      target_block_id,
      target_page_id,
    };
    return this.http.post<any[]>(
      `${this.environment.sigma_exporter_api}/import-question-bank`,
      payload,
    );
  }
}
